import { Aside } from '@psl/appComponents/Aside';
import {
  adaptiveWrapperOptions,
  ModalWrapper,
} from '@psl/appComponents/ModalWrapper';
import { UserInterfaceIdiomProvider } from '@psl/appComponents/UserInterfaceIdiomContext';
import { useIsMobileLayout } from '@psl/components/styles/responsive';
import type { EditListingDetailPageProps } from '@psl/flows/catalog/pages/EditListingDetailPage';
import { screenOptionsForLightMode } from '@psl/functions/navigation/screenOptions';
import { wrapLazyNavigatorScreen } from '@psl/functions/navigation/utils/wrapNavigatorScreen';
import { useResponsiveContext } from '@psl/functions/responsive/ResponsiveContext';
import { lazy } from 'react';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

const EditingListFlowContent = lazy(
  () => import('./components/EditingListFlowContent'),
);

export const EditingListingFlow = wrapLazyNavigatorScreen(
  ({
    nonMobileLayout,
    dismissible,
    ...props
  }: EditListingDetailPageProps & {
    nonMobileLayout?: 'modal' | 'aside';
    dismissible?: boolean;
  }) => {
    const isMobile = useIsMobileLayout();
    const { rpx } = useResponsiveContext();
    const { bottom, top } = useSafeAreaInsets();
    if (isMobile) {
      return (
        <UserInterfaceIdiomProvider
          name="listing-editor"
          userInterfaceStyle="light"
        >
          <SafeAreaInsetsContext.Provider
            value={{
              left: 0,
              right: 0,
              // note(@zhigang1992): fix react navigation bug on presentation: modal style
              top,
              bottom,
            }}
          >
            <EditingListFlowContent {...props} />
          </SafeAreaInsetsContext.Provider>
        </UserInterfaceIdiomProvider>
      );
    } else {
      if (nonMobileLayout === 'modal') {
        return (
          <UserInterfaceIdiomProvider
            name="settings"
            userInterfaceStyle="light"
          >
            <ModalWrapper
              dismissible={dismissible ?? true}
              width={rpx(400)}
              height={rpx(750)}
            >
              <EditingListFlowContent {...props} />
            </ModalWrapper>
          </UserInterfaceIdiomProvider>
        );
      } else {
        return (
          <UserInterfaceIdiomProvider
            name="settings"
            userInterfaceStyle="light"
          >
            <Aside dismissible={dismissible ?? true}>
              <EditingListFlowContent {...props} />
            </Aside>
          </UserInterfaceIdiomProvider>
        );
      }
    }
  },
  adaptiveWrapperOptions(isMobile =>
    isMobile
      ? screenOptionsForLightMode({
          presentation: 'card',
        })
      : screenOptionsForLightMode({
          presentation: 'transparentModal',
          cardOverlayEnabled: true,
          cardStyle: { backgroundColor: 'transparent' },
          animationEnabled: false,
          headerShown: false,
        }),
  ),
);
